import React from 'react';
import classNames from 'classnames';
import i18next from 'i18next';
import PropTypes from 'prop-types';

import { getPlaymakerMetadata } from 'components/packages/Playmaker/utils/getMetadata';
import Playmaker from 'components/packages/Playmaker';
import VideoPlayer from 'components/VideoPlayer';

import { content as ContentPropType } from 'lib/CustomPropTypes';
import { isVideoLive } from 'lib/videoUtils';

const block = 'cover-spread-featured-video';

class FeaturedVideo extends React.Component {
  static propTypes = {
    content: ContentPropType.isRequired,
    hasClickedToPlay: PropTypes.bool,
    isRailAdjacent: PropTypes.bool,
  };

  static defaultProps = {
    hasClickedToPlay: false,
    isRailAdjacent: false,
  };

  constructor(props) {
    super(props);

    const {
      content: {
        metadata: {
          playmakerToggle,
          playmakerPlayBehavior,
        } = {},
      },
    } = props;

    this.state = {
      isLive: false,
      hasClickedToPlay: (playmakerToggle && playmakerPlayBehavior === 'autoplay') || false,
    };
  }

  getVideoPlayer = () => {
    const { content: { metadata } } = this.props;
    const {
      playmakerToggle,
    } = metadata || {};

    if (playmakerToggle === true) {
      return this.getPlaymakerPlayer();
    }
    return this.getNormalPlayer();
  };

  getPlaymakerPlayer = () => {
    const { content, hasClickedToPlay } = this.props;
    return (
      <Playmaker
        content={content}
        handleStatus={this.handleStatus}
        hasClickedToPlay={hasClickedToPlay}
        onPlayClick={this.videoStarted}
      />
    );
  };

  getNormalPlayer = () => {
    const { content: { item }, hasClickedToPlay, isRailAdjacent } = this.props;
    return (
      <VideoPlayer
        centerPlayButtonAtMobile
        hasClickedToPlay={hasClickedToPlay}
        onAdPlay={this.videoStarted}
        onVideoPlay={this.videoStarted}
        onVideoEnd={this.videoEnded}
        replay
        video={item}
        isRailAdjacent={isRailAdjacent}
      />
    );
  };

  getTease = () => {
    const { content, isRailAdjacent } = this.props;
    const {
      computedValues: { headline: videoHeadline } = {},
      item: videoItem = {},
      metadata,
    } = content;

    const { flag } = metadata || {};

    const {
      playmakerFlag,
      playmakerToggle,
      playmakerTitle,
    } = getPlaymakerMetadata(metadata);

    const headline = playmakerToggle
      ? playmakerTitle
      : videoHeadline || videoItem.headline.primary;

    // 1. If video is live, show live flag with editorial text (if provided)
    const isLiveVideo = this.isLiveVideo(videoItem);
    // 2. If editors set custom flag text (whether live or not), show that text
    const customFlag = playmakerToggle ? playmakerFlag : flag;

    // If either live or custom text is set, show the flag.
    const showFlag = isLiveVideo || customFlag;

    return (
      <h2 className={`${block}__headline`} data-test={`${block}__headline`}>
        {showFlag && (
          <span
            className={
              classNames(
                `${block}__headline__flag`,
                { 'cover-spread-featured-video__headline__flag--live': isLiveVideo },
                { 'cover-spread-featured-video__headline__flag--rail': isRailAdjacent },
              )
            }
            data-test={isLiveVideo ? `${block}__headline__flag--live` : `${block}__headline__flag`}
          >
            { isLiveVideo && (
            <span
              className={`${block}__headline__flag__dot`}
              data-test={`${block}__headline__flag__dot`}
            />
            )}
            {customFlag || i18next.t('LIVE')}
            &nbsp;
            {'/ '}
          </span>
        )}
        <span
          className={classNames(
            `${block}__headline__text`,
            { 'cover-spread-featured-video__headline__text--rail': isRailAdjacent },
          )}
          data-test={`${block}__headline__text`}
        >
          {headline}
        </span>
      </h2>
    );
  };

  handleStatus = (status, isLive) => {
    this.setState({ isLive });
  };

  videoStarted = () => {
    const { hasClickedToPlay } = this.props;

    // Update parent component
    if (!hasClickedToPlay) {
      this.setState({ hasClickedToPlay: true });
    }
  };

  videoEnded = () => {
    const { hasClickedToPlay } = this.props;

    if (hasClickedToPlay) {
      this.setState({
        hasClickedToPlay: false,
      });
    }
  };

  isLiveVideo(item) {
    const { isLive } = this.state;
    if (isLive) return true;
    return isVideoLive(item);
  }

  render() {
    const { content, isRailAdjacent } = this.props;
    const { hasClickedToPlay } = this.state;

    return (
      <div
        className={classNames(block, { [`${block}--playing`]: hasClickedToPlay, [`${block}--rail`]: isRailAdjacent })}
        data-contentid={content.id || 'playmaker'}
      >
        <div className={`${block}__player`}>
          { this.getVideoPlayer() }
        </div>
        { this.getTease() }
      </div>
    );
  }
}

export default FeaturedVideo;
